/**
 * Attachments panel component for managing essay file attachments.
 * 
 * Features:
 * - Drag-and-drop file upload
 * - File list with icons by type
 * - Download links
 * - Public/private visibility toggle (author only)
 * - Delete button (author only)
 * - File size display
 * - Upload progress indicator
 * 
 * Called by:
 * - EssayDetailPage for displaying and managing attachments
 */
import { useState } from 'react';
import {
  Upload,
  List,
  Button,
  Space,
  Typography,
  Switch,
  Popconfirm,
  message,
  Empty,
  Spin,
} from 'antd';
import {
  InboxOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileTextOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  uploadAttachments,
  deleteAttachment,
  setAttachmentVisibility,
  getAttachmentUrl,
} from '../services/api';
import type { Attachment, User } from '@compendium/shared';

const { Text, Title } = Typography;
const { Dragger } = Upload;

// =============================================================================
// Props Interface
// =============================================================================

interface AttachmentsPanelProps {
  /** Essay slug for API calls */
  slug: string;
  /** List of attachments to display */
  attachments: Attachment[];
  /** Whether attachments are loading */
  isLoading?: boolean;
  /** Current user for permission checks */
  user: User;
  /** Callback when attachments change */
  onAttachmentsChange?: () => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get appropriate icon for file type based on MIME type.
 */
function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) {
    return <FileImageOutlined style={{ fontSize: 24, color: '#52c41a' }} />;
  }
  if (mimeType === 'application/pdf') {
    return <FilePdfOutlined style={{ fontSize: 24, color: '#f5222d' }} />;
  }
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return <FileWordOutlined style={{ fontSize: 24, color: '#5b9bd5' }} />;
  }
  if (mimeType.startsWith('text/')) {
    return <FileTextOutlined style={{ fontSize: 24, color: '#722ed1' }} />;
  }
  return <FileOutlined style={{ fontSize: 24, color: '#8c8c8c' }} />;
}

/**
 * Format file size for display.
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Attachments panel component.
 * Allows uploading, viewing, and managing file attachments for essays.
 */
function AttachmentsPanel({
  slug,
  attachments,
  isLoading = false,
  user,
  onAttachmentsChange,
}: AttachmentsPanelProps) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const isAuthor = user.role === 'author';

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => uploadAttachments(slug, files),
    onSuccess: () => {
      message.success('Files uploaded successfully');
      queryClient.invalidateQueries({ queryKey: ['attachments', slug] });
      onAttachmentsChange?.();
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to upload files');
    },
    onSettled: () => {
      setUploading(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteAttachment,
    onSuccess: () => {
      message.success('Attachment deleted');
      queryClient.invalidateQueries({ queryKey: ['attachments', slug] });
      onAttachmentsChange?.();
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to delete attachment');
    },
  });

  // Visibility mutation
  const visibilityMutation = useMutation({
    mutationFn: ({ id, isPublic }: { id: number; isPublic: boolean }) =>
      setAttachmentVisibility(id, isPublic),
    onSuccess: () => {
      message.success('Visibility updated');
      queryClient.invalidateQueries({ queryKey: ['attachments', slug] });
      onAttachmentsChange?.();
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to update visibility');
    },
  });

  /**
   * Handle file upload via drag-and-drop or click.
   */
  const uploadProps: UploadProps = {
    name: 'files',
    multiple: true,
    accept: 'image/*,.pdf,.doc,.docx,.txt,.md',
    beforeUpload: (file, fileList) => {
      // Only process on the last file in the batch
      const isLast = fileList[fileList.length - 1] === file;
      if (isLast) {
        setUploading(true);
        uploadMutation.mutate(fileList as unknown as File[]);
      }
      // Prevent default upload behavior - we handle it manually
      return false;
    },
    showUploadList: false,
    disabled: !isAuthor,
  };

  return (
    <div style={{ padding: '16px 0' }}>
      <Title level={5} style={{ marginBottom: 16 }}>
        Attachments
      </Title>

      {/* Upload area (author only) */}
      {isAuthor && (
        <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag files to upload
          </p>
          <p className="ant-upload-hint">
            Images, PDFs, documents (max 10MB each)
          </p>
        </Dragger>
      )}

      {/* Upload progress */}
      {uploading && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin tip="Uploading..." />
        </div>
      )}

      {/* Loading state */}
      {isLoading && !uploading && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin tip="Loading attachments..." />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && attachments.length === 0 && (
        <Empty
          description="No attachments"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* Attachment list */}
      {!isLoading && attachments.length > 0 && (
        <List
          itemLayout="horizontal"
          dataSource={attachments}
          renderItem={(attachment) => (
            <List.Item
              style={{
                background: '#242424',
                borderRadius: 8,
                marginBottom: 8,
                padding: '12px 16px',
              }}
              actions={[
                // Download button
                <Button
                  key="download"
                  type="text"
                  icon={<DownloadOutlined />}
                  href={getAttachmentUrl(attachment.id)}
                  target="_blank"
                  title="Download"
                />,
                // Visibility toggle (author only)
                ...(isAuthor
                  ? [
                      <Switch
                        key="visibility"
                        checked={attachment.isPublic}
                        checkedChildren={<EyeOutlined />}
                        unCheckedChildren={<EyeInvisibleOutlined />}
                        onChange={(checked) =>
                          visibilityMutation.mutate({
                            id: attachment.id,
                            isPublic: checked,
                          })
                        }
                        title={attachment.isPublic ? 'Public' : 'Private'}
                        loading={visibilityMutation.isPending}
                      />,
                    ]
                  : []),
                // Delete button (author only)
                ...(isAuthor
                  ? [
                      <Popconfirm
                        key="delete"
                        title="Delete attachment?"
                        description="This cannot be undone."
                        onConfirm={() => deleteMutation.mutate(attachment.id)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          loading={deleteMutation.isPending}
                          title="Delete"
                        />
                      </Popconfirm>,
                    ]
                  : []),
              ]}
            >
              <List.Item.Meta
                avatar={getFileIcon(attachment.mimeType)}
                title={
                  <a
                    href={getAttachmentUrl(attachment.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#f5f5f5' }}
                  >
                    {attachment.filename}
                  </a>
                }
                description={
                  <Space size="middle">
                    <Text type="secondary">{formatFileSize(attachment.size)}</Text>
                    <Text type="secondary">
                      {new Date(attachment.createdAt).toLocaleDateString()}
                    </Text>
                    {attachment.isPublic ? (
                      <Text type="success" style={{ fontSize: 12 }}>
                        Public
                      </Text>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Private
                      </Text>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
}

export default AttachmentsPanel;
